import type { PushBrowser, PushListing } from "@omnabase/shared";
import { api } from "./api";
import { authClient } from "./auth";
import { getRouter } from "./router";
import { keyBytes } from "./pushCard";
import { showAppBadge } from "./appBadge";
import { browserWord, deviceKind, isStandalone, type DeviceKind } from "./device";
import { markDeliberateSignOut } from "./signedOut";

/** Browser notifications round one (2026-10-18), the browser's side: whether this browser can get pop-ups, turning them on and off for one
 *  workspace, forgetting them on signing out (decision 7), and moving an open tab to the item a pop-up's click names. "Turn on" is per
 *  workspace (decision 2); which of the workspace's rows is this browser's is remembered here, per workspace. */
const ROW_KEY = "ob-push-row:";
const route = (slug: string) => `/api/v1/w/${encodeURIComponent(slug)}/me/push-subscriptions`;

/** the browser can subscribe: the API exists and the page is a secure context */
/** the background script's version, the number on `apps/web/sw.js`'s first line; bump both together */
export const SW_VERSION = "1";
export const pushSupported = () => typeof window !== "undefined" && window.isSecureContext && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
/** the notification permission: a grant the Permissions API reports wins (headless Chrome answers "denied" from `Notification.permission`
 *  after a grant; real browsers agree on both), else `Notification.permission` */
export async function permissionNow(): Promise<NotificationPermission | null> {
  if (typeof Notification === "undefined") return null;
  try { if ((await navigator.permissions?.query({ name: "notifications" }))?.state === "granted") return "granted"; } catch { /* no Permissions API: the page's own answer */ }
  return Notification.permission;
}

function storage(): Storage | null { try { return window.localStorage; } catch { return null; } }
export const rememberedRow = (slug: string): string | null => { try { return storage()?.getItem(ROW_KEY + slug) ?? null; } catch { return null; } };
const remember = (slug: string, id: string | null) => { try { if (id) storage()?.setItem(ROW_KEY + slug, id); else storage()?.removeItem(ROW_KEY + slug); } catch { /* private window: the card shows Off next time */ } };
/** the workspaces this browser remembers being turned on in */
const rememberedSlugs = (): string[] => { const s = storage(); if (!s) return []; try { return Object.keys(s).filter((k) => k.startsWith(ROW_KEY)).map((k) => k.slice(ROW_KEY.length)); } catch { return []; } };

export const listBrowsers = (slug: string) => api.get<PushListing>(route(slug));

/** this device, read once from the browser (the device notifications round, 2026-11-01): its kind (an iPad by its touch screen), whether it
 *  runs from the Home Screen, and the browser that asks the permission question */
export type ThisDevice = { kind: DeviceKind; standalone: boolean; browser: string | null };
export function thisDevice(): ThisDevice {
  if (typeof window === "undefined") return { kind: "other", standalone: false, browser: null };
  const ua = navigator.userAgent, standalone = isStandalone(window);
  return { kind: deviceKind(ua, { standalone, touchPoints: navigator.maxTouchPoints }), standalone, browser: browserWord(ua) };
}
/** what the server stores for this device: the subscription, and the iPad's mark its Mac-like browser description hides */
export const subscriptionBody = (sub: PushSubscription) => ({ ...sub.toJSON(), ...(thisDevice().kind === "ipad" ? { device: "ipad" } : {}) });

/** this browser's subscription, if it has one (no background script is installed just to look) */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration("/");
  return reg ? reg.pushManager.getSubscription() : null;
}

export type TurnOn = { ok: true; row: PushBrowser } | { ok: false; reason: "denied" | "dismissed" };
/** "Turn on": the permission first, inside the click (Safari refuses a request after an await), then the background script, the subscription
 *  with the server's key, and the browser's row in this workspace */
export async function turnOn(slug: string, publicKey: string): Promise<TurnOn> {
  const asked = await Notification.requestPermission();
  const permission = asked === "granted" ? asked : await permissionNow();
  if (permission !== "granted") return { ok: false, reason: permission === "denied" ? "denied" : "dismissed" };
  // the version in the address (2026-10-18): a cache in front of the app (Cloudflare held the SPA page under /sw.js for four hours after the
  // first deploy) never serves a stale copy for a new version, and a new version re-registers by itself; the API's route ignores the query
  await navigator.serviceWorker.register(`/sw.js?v=${SW_VERSION}`, { scope: "/" });
  const reg = await navigator.serviceWorker.ready;
  const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) }));
  const r = await api.put<{ subscription: PushBrowser }>(route(slug), subscriptionBody(sub));
  remember(slug, r.subscription.id);
  return { ok: true, row: r.subscription };
}

/** "Turn off": this browser's row in this workspace goes, and the browser's own subscription is cancelled unless another workspace in this
 *  browser still uses it */
export async function turnOff(slug: string, rowId: string): Promise<void> {
  await api.del(`${route(slug)}/${encodeURIComponent(rowId)}`);
  remember(slug, null);
  if (rememberedSlugs().length) return;
  await (await currentSubscription())?.unsubscribe();
}

export const removeBrowser = (slug: string, rowId: string) => api.del(`${route(slug)}/${encodeURIComponent(rowId)}`);
/** the forum sync round (2026-11-02, brief rule 7): one test pop-up to this device through the real push path; answers the push service's verdict */
export const testBrowser = (slug: string, rowId: string) => api.post<{ outcome: TestOutcome }>(`${route(slug)}/${encodeURIComponent(rowId)}/test`, {});
export type TestOutcome = "delivered" | "gone" | "failed" | "ourKey";

/** Signing out (decision 7): the badge is cleared, the browser's address goes along in a header so the server forgets it in every workspace,
 *  then the browser's own subscription is cancelled, so the next person signing in here never inherits the pop-ups */
export async function signOutHere(): Promise<void> {
  markDeliberateSignOut(); // the forum sync round: a Sign out the person chose never shows the signed-out sheet
  void showAppBadge(0); // round two (2026-10-18): the next person at this computer never sees the last one's queue count on the icon
  const sub = await currentSubscription().catch(() => null);
  await authClient.signOut(sub ? { fetchOptions: { headers: { "x-push-endpoint": sub.endpoint } } } : undefined);
  for (const slug of rememberedSlugs()) remember(slug, null);
  await sub?.unsubscribe().catch(() => false);
}

/** a pop-up's click in a tab the background script does not control: it sends the item's address, and the open app moves there */
export function followPopUpClicks(): void {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  navigator.serviceWorker.addEventListener("message", (e: MessageEvent) => {
    const data = e.data as { type?: unknown; url?: unknown } | null;
    if (data?.type !== "omna-open" || typeof data.url !== "string") return;
    const u = new URL(data.url, window.location.origin);
    if (u.origin !== window.location.origin) return;
    const to = `${u.pathname}${u.search}${u.hash}`;
    const r = getRouter();
    if (r) void r.navigate(to); else window.location.assign(to);
  });
}
